import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { apiFetch } from '../lib/api';

/**
 * Legal pages. Each page checks site_content for an admin-uploaded version
 * (a parsed .docx published via /admin/site-content). If one exists, we
 * render it. If not, we fall back to the hard-coded copy below.
 *
 * Update the EFFECTIVE_DATE constant when any fallback policy changes.
 */
const EFFECTIVE_DATE = 'Effective 21 April 2026';

interface PolicyContent {
  html?: string;
  source_filename?: string | null;
  updated_at?: string;
}

/** Fetch a single site_content key, return its parsed value (or null). */
function usePolicy(key: string): { content: PolicyContent | null; loading: boolean } {
  const [content, setContent] = useState<PolicyContent | null>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    apiFetch<Record<string, PolicyContent>>('/api/catalog/site-content')
      .then((all) => {
        const v = all[key];
        if (v && v.html) setContent(v);
      })
      .catch(() => {
        // ignore — fallback content renders
      })
      .finally(() => setLoading(false));
  }, [key]);
  return { content, loading };
}

interface SectionProps {
  num: number;
  title: string;
  children: ReactNode;
}

function LegalShell({
  title,
  intro,
  policyKey,
  children
}: {
  title: string;
  intro: string;
  policyKey: string;
  children: ReactNode;
}) {
  const { content, loading } = usePolicy(policyKey);
  return (
    <section className="screen">
      <div className="scroll legal-scroll">
        <div className="legal-head">
          <h1>{title}</h1>
          <div className="meta">
            Label Rotation ·{' '}
            {content?.updated_at
              ? `Updated ${new Date(content.updated_at).toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric'
                })}`
              : EFFECTIVE_DATE}
          </div>
        </div>
        {loading ? (
          <div className="legal-intro" style={{ opacity: 0.6 }}>
            Loading…
          </div>
        ) : content?.html ? (
          // Admin-uploaded HTML wins. mammoth's output is clean tagged HTML
          // with no inline scripts; we trust it.
          <div
            className="legal-doc"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: content.html }}
          />
        ) : (
          <>
            <div className="legal-intro">{intro}</div>
            {children}
          </>
        )}
        <div style={{ height: 40 }} />
      </div>
    </section>
  );
}

function Section({ num, title, children }: SectionProps) {
  return (
    <div className="legal-section">
      <div className="num">SECTION {num}</div>
      <h2>{title}</h2>
      {children}
    </div>
  );
}

export function PrivacyPolicy() {
  return (
    <LegalShell
      title="PRIVACY POLICY"
      policyKey="policy_privacy"
      intro="This Privacy Policy explains how Label Rotation collects, uses, stores, and protects your personal data when you use our services at labelrotation.co.uk, in accordance with the UK GDPR and the Data Protection Act 2018."
    >
      <Section num={1} title="WHO WE ARE">
        <p>
          Label Rotation is operated as a sole trader business and is the data controller
          responsible for your personal data.
        </p>
        <div className="contact-row">
          <span className="lbl">Address</span>
          <span className="val">Suite 596, 80A Ruskin Ave, Welling DA16 3QQ</span>
        </div>
        <div className="contact-row">
          <span className="lbl">Email</span>
          <span className="val">
            <a href="mailto:team@labelrotation.co.uk">team@labelrotation.co.uk</a>
          </span>
        </div>
      </Section>
      <Section num={2} title="WHAT WE COLLECT">
        <p>
          We collect account data (name, email, date of birth), order and transaction data,
          payment data via Stripe, delivery and contact data, and technical usage data such
          as IP address and browser information.
        </p>
        <div className="em">
          We do not store full card numbers. Payment data is processed and stored by Stripe
          in accordance with PCI DSS standards.
        </div>
      </Section>
      <Section num={3} title="WHY WE USE YOUR DATA">
        <p>
          We process your data to fulfil your order, process payments, send transactional
          emails, verify your identity, manage our relationship with you, comply with legal
          obligations, and improve our website.
        </p>
      </Section>
      <Section num={4} title="YOUR RIGHTS">
        <p>
          Under UK GDPR you have the right to access, rectify, erase, restrict, port, and
          object to the processing of your data. To exercise these rights, contact us at{' '}
          <a href="mailto:team@labelrotation.co.uk?subject=Data%20Request">
            team@labelrotation.co.uk
          </a>
          .
        </p>
      </Section>
    </LegalShell>
  );
}

export function TermsAndConditions() {
  return (
    <LegalShell
      title="TERMS & CONDITIONS"
      policyKey="policy_terms"
      intro="These Terms & Conditions govern your use of Label Rotation and our clothing rental services. By creating an account or placing an order, you agree to be bound by these Terms."
    >
      <Section num={1} title="CREDIT SYSTEM">
        <p>
          Items are rented using credits purchased in bundles. Credits have no cash value,
          are non-transferable, and never expire. A minimum spend of 3 credits is required
          per checkout, except where refunded credits are used.
        </p>
      </Section>
      <Section num={2} title="RENTAL PERIOD">
        <p>
          Each rental lasts 30 days from confirmed delivery. A 2-day grace period applies;
          items must be in transit by Day 32. Late fees of £10 per day apply from Day 33.
        </p>
      </Section>
      <Section num={3} title="PAYMENTS">
        <p>
          By placing an order you authorise Label Rotation to charge your saved payment
          method for any late fees, damage fees, or replacement value charges that become
          due under these Terms.
        </p>
      </Section>
    </LegalShell>
  );
}

export function ReturnsPolicy() {
  return (
    <LegalShell
      title="RETURNS & RENTAL POLICY"
      policyKey="policy_returns"
      intro="This policy explains how Label Rotation rentals work, how to return items, and what happens if something goes wrong."
    >
      <Section num={1} title="HOW TO RETURN">
        <p>
          Use the prepaid return label we email between Day 25–30. Reuse the original
          mailing bag, seal it securely, and drop it at your nearest collection point.
          Always keep your proof of postage.
        </p>
      </Section>
      <Section num={2} title="FIT RETURNS">
        <p>
          Fit issues must be reported within 48 hours of delivery. Items must be unworn
          other than trying on. Credits will be refunded once the returned item has been
          inspected. A £4.99 delivery fee applies when refunded credits are used on a new
          order.
        </p>
      </Section>
    </LegalShell>
  );
}

export function DamageLossPolicy() {
  return (
    <LegalShell
      title="DAMAGE & LOSS POLICY"
      policyKey="policy_damage_loss"
      intro="Normal wear is expected and never charged. A damage fee only applies if an item cannot be returned to the rotation."
    >
      <Section num={1} title="CHARGEABLE DAMAGE">
        <p>
          Damage fees apply for permanent stains, tears, rips, burns, persistent odour,
          structural damage to fastenings, or any alteration. The fee for each item is
          listed on its product page. We will always notify you at least 72 hours before
          applying any charge.
        </p>
      </Section>
      <Section num={2} title="LOSS & NON-RETURN">
        <p>
          If an item is lost or cannot be returned, the full replacement value will be
          charged. Where you have valid proof of postage using our prepaid label, you are
          protected if the parcel is lost in transit.
        </p>
      </Section>
    </LegalShell>
  );
}

export function CookiePolicy() {
  return (
    <LegalShell
      title="COOKIE POLICY"
      policyKey="policy_cookies"
      intro="We use essential cookies (session management, authentication, payments via Stripe) and optional analytics cookies. Analytics cookies are only set with your consent."
    >
      <Section num={1} title="MANAGING COOKIES">
        <p>
          You can change your cookie preferences at any time via your browser settings.
          Blocking essential cookies will affect core site functionality including login
          and checkout.
        </p>
      </Section>
    </LegalShell>
  );
}

/** Combined customer-facing returns + damage doc — shown at /returns-damage. */
export function ReturnsAndDamage() {
  return (
    <LegalShell
      title="RETURNS & DAMAGE"
      policyKey="policy_returns_damage"
      intro="A combined plain-English guide to how returns and any damage charges work. For the full legal text, see the Returns Policy and Damage & Loss Policy."
    >
      <Section num={1} title="RETURNS — THE SHORT VERSION">
        <p>
          Each rental lasts 30 days. We email a prepaid return label between Day 25 and
          30. Drop the items in the mailing bag we sent and post them at any collection
          point. Always keep your proof of postage.
        </p>
      </Section>
      <Section num={2} title="WHEN YOU MIGHT BE CHARGED">
        <p>
          Normal wear is expected and never charged. A damage fee only applies if a piece
          can&apos;t be returned to the rotation — permanent stains, tears, or persistent
          odour. The fee for each item is shown on its product page and we always notify
          you 72 hours before any charge.
        </p>
      </Section>
    </LegalShell>
  );
}
